<template>
  <div class="levelrule" v-loading="loading">
    <div class="levelrule-header">
      <el-button type="primary" @click="handleSave" :loading="saving">
        保存配置
      </el-button>
    </div>
    <el-table :data="displayList" border class="levelrule-table">
      <el-table-column label="等级" width="100" align="center">
        <template #default="{ $index }">
          <span class="level-badge">Lv.{{ $index + 1 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="等级名称" min-width="150">
        <template #default="{ row, $index }">
          <span v-if="$index === 0">{{ row.name }}</span>
          <el-input v-else v-model="row.name" placeholder="请输入等级名称" />
        </template>
      </el-table-column>
      <el-table-column label="等级颜色" width="180">
        <template #default="{ row, $index }">
          <div v-if="$index === 0" class="color-display">
            <span>{{ getColorLabel(row.color) }}</span>
          </div>
          <el-select v-else v-model="row.color" placeholder="请选择颜色">
            <el-option
              v-for="item in colorOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
              <span class="color-block" :style="{ backgroundColor: item.value }" />
              <span>{{ item.label }}</span>
            </el-option>
          </el-select>
        </template>
      </el-table-column>
      <el-table-column label="所需成长值" min-width="150">
        <template #default="{ row, $index }">
          <span v-if="$index === 0">{{ row.growthValue }}</span>
          <el-input-number
            v-else
            v-model="row.growthValue"
            :min="$index === 1 ? 1 : (levelList[$index - 2]?.growthValue || 0) + 1"
            :controls="false"
            placeholder="请输入所需成长值"
            style="width: 100%"
          />
        </template>
      </el-table-column>
      <el-table-column label="折扣" width="150">
        <template #default="{ row, $index }">
          <span v-if="$index === 0">-</span>
          <el-input-number
            v-else
            v-model="row.discount"
            :min="0.01"
            :max="9.99"
            :precision="2"
            :step="0.1"
            :controls="false"
            placeholder="可选"
            style="width: 100%"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" align="center">
        <template #default="{ $index }">
          <template v-if="$index === 0">
            <span class="disabled-text">默认等级</span>
          </template>
          <el-button-group v-else>
            <el-button size="small" @click="handleInsertBefore($index - 1)" :icon="Top">
              前增
            </el-button>
            <el-button size="small" @click="handleInsertAfter($index - 1)" :icon="Bottom">
              后增
            </el-button>
            <el-button
              size="small"
              type="danger"
              @click="handleDelete($index - 1)"
              :icon="Delete"
              :disabled="levelList.length <= 1"
            />
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="levelList.length === 0" class="empty-tip">
      <el-button type="primary" @click="handleAdd">添加第一个等级</el-button>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useApi } from '@/hooks/useApi';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, Top, Bottom } from '@element-plus/icons-vue';
import { useBaseAsync } from 'hpsfir-managebase-ui';

// 本地使用的等级规则类型（数值类型）
interface LocalLevelRule {
  name: string;
  color: string;
  growthValue: number;
  discount?: number;
}

// 颜色选项
const colorOptions = [
  { value: '#36643f', label: '墨绿' },
  { value: '#3c6585', label: '蓝灰' },
  { value: '#b6860f', label: '金棕' },
  { value: '#2158ba', label: '藏蓝' },
  { value: '#705481', label: '紫棕' },
  { value: '#7173d2', label: '灰紫' },
  { value: '#b23323', label: '砖红' },
];

// 默认等级（不存数据库）
const defaultLevel: LocalLevelRule = {
  name: '普通',
  color: '#999999',
  growthValue: 0,
  discount: undefined,
};

const levelList = ref<LocalLevelRule[]>([]);
const saving = ref(false);
const loading = ref(false);

// 显示列表（包含默认等级）
const displayList = computed(() => [defaultLevel, ...levelList.value]);

// 获取颜色标签
const getColorLabel = (color: string) => {
  if (color === '#999999') {
    return '灰白';
  }
  const option = colorOptions.find((item) => item.value === color);
  return option ? option.label : color;
};

// 创建空等级
const createEmptyLevel = (growthValue = 1): LocalLevelRule => ({
  name: '',
  color: '#36643f',
  growthValue,
  discount: undefined,
});

// 加载配置
const loadConfig = useBaseAsync(async() => {
  loading.value = true;
  try {
    const res = await useApi().userLevelRuleApi.listUserLevelRuleuserLevelRule();
    const rules = res.data.data || [];
    if (rules.length > 0) {
      // 后端返回字符串，转换为数字
      levelList.value = rules.map((rule) => ({
        ...rule,
        growthValue: rule.growthValue === undefined ? 0 : Number(rule.growthValue),
        discount: rule.discount === undefined || rule.discount === '' ? undefined : Number(rule.discount),
      }));
    } else {
      levelList.value = [createEmptyLevel()];
    }
  } finally {
    loading.value = false;
  }
});

// 保存配置
const handleSave = useBaseAsync(async() => {
  // 验证
  for (let i = 0; i < levelList.value.length; i++) {
    const level = levelList.value[i];
    if (!level.name) {
      ElMessage.error(`第 ${i + 1} 级的等级名称不能为空`);
      return;
    }
    if (level.growthValue === undefined || level.growthValue === null) {
      ElMessage.error(`第 ${i + 1} 级的所需成长值不能为空`);
      return;
    }
    if (i > 0 && level.growthValue <= levelList.value[i - 1].growthValue) {
      ElMessage.error(`第 ${i + 1} 级的成长值必须大于第 ${i} 级`);
      return;
    }
    if (level.discount !== undefined && level.discount !== null) {
      if (level.discount <= 0 || level.discount >= 10) {
        ElMessage.error(`第 ${i + 1} 级的折扣必须大于0且小于10`);
        return;
      }
    }
  }

  saving.value = true;
  try {
    // 数字转换为字符串发送给后端
    const rulesData = levelList.value.map((rule) => ({
      ...rule,
      growthValue: String(rule.growthValue),
      discount: rule.discount === undefined ? undefined : String(rule.discount),
    }));
    await useApi().userLevelRuleApi.setUserLevelRulesuserLevelRule({
      rules: rulesData,
    });
    ElMessage.success('保存成功');
  } finally {
    saving.value = false;
  }
});

// 在前面插入
const handleInsertBefore = (index: number) => {
  const prevGrowthValue = index > 0 ? levelList.value[index - 1].growthValue : 0;
  const currentGrowthValue = levelList.value[index].growthValue;
  const newGrowthValue = Math.floor((prevGrowthValue + currentGrowthValue) / 2);
  levelList.value.splice(index, 0, createEmptyLevel(newGrowthValue));
};

// 在后面插入
const handleInsertAfter = (index: number) => {
  const currentGrowthValue = levelList.value[index].growthValue;
  const nextGrowthValue = index < levelList.value.length - 1
    ? levelList.value[index + 1].growthValue
    : currentGrowthValue + 100;
  const newGrowthValue = Math.floor((currentGrowthValue + nextGrowthValue) / 2);
  levelList.value.splice(index + 1, 0, createEmptyLevel(newGrowthValue));
};

// 删除
const handleDelete = async(index: number) => {
  await ElMessageBox.confirm('确定要删除该等级吗？', '提示', {
    type: 'warning',
    confirmButtonText: '确认',
    cancelButtonText: '取消',
  });
  levelList.value.splice(index, 1);
};

// 添加第一个等级
const handleAdd = () => {
  levelList.value.push(createEmptyLevel());
};

onMounted(() => {
  loadConfig();
});
</script>
<style lang="scss" scoped>
@import './index.scss';
</style>
